import type { NotebookNote } from '../../shared/types/notebook'

export type DropPosition = 'before' | 'inside' | 'after'

/** Determine drop position based on cursor Y within the row rect */
export function computeDropPosition(cursorY: number, rowRect: DOMRect): DropPosition {
  const relativeY = cursorY - rowRect.top
  const rowHeight = rowRect.height
  const ratio = relativeY / rowHeight

  if (ratio < 0.3) return 'before'
  if (ratio > 0.7) return 'after'
  return 'inside'
}

/** Check if gap between two sort orders is too small and needs reindexing */
export function needsReindex(prevOrder: number | null, nextOrder: number | null): boolean {
  if (prevOrder === null || nextOrder === null) return false
  return Math.abs(nextOrder - prevOrder) < 2
}

/** Re-index siblings under a parent with gap-based spacing (0, 1000, 2000...) */
export function reindexSiblings(
  notes: NotebookNote[],
  parentId: string | null,
): Array<{ id: string; sortOrder: number }> {
  const siblings = notes
    .filter(n => n.parentId === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  return siblings.map((note, i) => ({
    id: note.id,
    sortOrder: i * 1000,
  }))
}

/** Get all descendant IDs of a note (recursive) */
function getAllDescendantIds(notes: NotebookNote[], noteId: string): string[] {
  const result: string[] = []
  const children = notes.filter(n => n.parentId === noteId)
  for (const child of children) {
    result.push(child.id)
    result.push(...getAllDescendantIds(notes, child.id))
  }
  return result
}

/** Check if targetId is a descendant of ancestorId */
export function isDescendantOf(
  notes: NotebookNote[],
  targetId: string,
  ancestorId: string,
): boolean {
  const descendants = getAllDescendantIds(notes, ancestorId)
  return descendants.includes(targetId)
}

